import React, { useState } from "react";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Button,
  Typography,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";

import CheckoutForm from "../CheckoutForm";
import { createPayment } from "../../offchain-logic/services";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

// Interfaccia per i dati di ogni attività
interface ActivityData {
  id: string; // ID univoco generato automaticamente
  indirizzo: string;
  citta: string;
  cap: string;
  provincia: string;
  telefono: string; // Opzionale
  email: string; // Opzionale
}

interface PaymentPageProps {
  open: boolean;
  handleClose: () => void;
  handlePaymentSuccess: () => void;
}

// Fullscreen Apple-like loader
const FullscreenLoader: React.FC<{ visible: boolean; label?: string }> = ({ visible, label = "Elaborazione…" }) => {
  if (!visible) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(6px)",
        zIndex: 1400,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <motion.div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            border: "5px solid #E6EEF9",
            borderTopColor: "#2D7FF9",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
        />
        <Typography variant="h6" sx={{ color: "#1f6feb", fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">Non chiudere la finestra — verrai reindirizzato automaticamente.</Typography>
      </Box>
    </motion.div>
  );
};

// Animated checkmark component (appears when payment succeeded)
const AnimatedCheckmark: React.FC = () => (
  <motion.div
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.5, type: "spring", stiffness: 260, damping: 20 }}
    style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}
  >
    <svg width="96" height="96" viewBox="0 0 120 120">
      <motion.circle
        cx="60"
        cy="60"
        r="50"
        stroke="#3BB273"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.45, ease: "easeInOut" }}
      />
      <motion.path
        d="M35 62 L52 80 L86 42"
        fill="none"
        stroke="#3BB273"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay: 0.3, duration: 0.45 }}
      />
    </svg>
  </motion.div>
);

const PaymentPageDialog: React.FC<PaymentPageProps> = ({ open, handleClose, handlePaymentSuccess }) => {
  // Step management: 0 = numero attività, 0.5 = form attività, 1 = tipo pagamento, 2 = stripe
  const [step, setStep] = useState<number>(0);
  const [numberOfActivities, setNumberOfActivities] = useState<number>(1);
  const [currentActivityIndex, setCurrentActivityIndex] = useState<number>(0);
  const [activitiesData, setActivitiesData] = useState<ActivityData[]>([]);
  
  const [paymentMode, setPaymentMode] = useState<"single" | "subscription">("single");
  const [clientSecret, setClientSecret] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingOverlay, setIsProcessingOverlay] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // CONFIG
  const COST_PER_ACTIVITY = 50; // Costo per ogni attività
  const BASE_SINGLE_PAYMENT = 300;
  const BASE_MONTHLY_COST = 25;
  const SUBSCRIPTION_MONTHS = 12;
  // Calcolo del costo totale in base al numero di attività
  // La prima attività è inclusa nel prezzo base, si pagano solo le attività aggiuntive
  const additionalActivities = Math.max(0, numberOfActivities - 1);
  const totalActivityCost = additionalActivities * COST_PER_ACTIVITY;
  const activityCostPerMonth = totalActivityCost / SUBSCRIPTION_MONTHS; // Spalma il costo attività sui 12 mesi
  
  const SINGLE_PAYMENT_AMOUNT = BASE_SINGLE_PAYMENT + totalActivityCost;
  const MONTHLY_COST = BASE_MONTHLY_COST + activityCostPerMonth;

  // Handler per confermare il numero di attività e inizializzare i form
  const handleNumberConfirm = () => {
    const emptyActivities: ActivityData[] = Array(numberOfActivities).fill(null).map(() => ({
      id: crypto.randomUUID(), // Genera ID univoco per ogni attività
      indirizzo: "",
      citta: "",
      cap: "",
      provincia: "",
      telefono: "",
      email: ""
    }));
    setActivitiesData(emptyActivities);
    setCurrentActivityIndex(0);
    setStep(0.5);
  };

  // Handler per salvare i dati di un'attività
  const handleActivitySave = () => {
    if (currentActivityIndex < numberOfActivities - 1) {
      setCurrentActivityIndex(currentActivityIndex + 1);
    } else {
      setStep(1);
    }
  };

  // Handler per tornare indietro nel form attività
  const handleActivityBack = () => {
    if (currentActivityIndex > 0) {
      setCurrentActivityIndex(currentActivityIndex - 1);
    } else {
      setStep(0);
    }
  };

  // Update activity data
  const updateActivityData = (field: keyof ActivityData, value: string) => {
    const newData = [...activitiesData];
    newData[currentActivityIndex] = {
      ...newData[currentActivityIndex],
      [field]: value
    };
    setActivitiesData(newData);
  };

  // Start payment creation
  const startPayment = async () => {
    setIsLoading(true);
    setError(null);
    setClientSecret("");

    try {
      // Prepara payload in base al mode
      const payload = paymentMode === "single"
        ? { 
            mode: "single", 
            amount: SINGLE_PAYMENT_AMOUNT,
            activities: activitiesData // Invia lista attività al backend
          }
        : { 
            mode: "subscription", 
            variableAmount: activityCostPerMonth, // Costo mensile aggiuntivo per le attività
            activities: activitiesData // Invia lista attività al backend
          };

      console.log("Promise payload: " + JSON.stringify(payload));
      await new Promise<void>((resolve) => {
        createPayment(payload, (err: any, res: any) => {
          setIsLoading(false);
          if (err || !res) {
            setError(err?.message || "Errore creazione intent");
            resolve();
            return;
          }
          if (res.clientSecret) {
            setClientSecret(res.clientSecret);
            setStep(2);
          } else {
            setError("Mancato clientSecret dal server");
          }
          resolve();
        });
      });
    } catch (err: any) {
      setIsLoading(false);
      setError(err?.message || "Errore inaspettato");
    }
  };

  // Handle local success
  const handleLocalSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      handlePaymentSuccess();
      handleClose();
    }, 1500);
  };

  const options: StripeElementsOptions = clientSecret ? { clientSecret, appearance: { theme: "stripe" } } : {};

  return (
    <>
      <AnimatePresence>
        {(isLoading || isProcessingOverlay) && (
          <FullscreenLoader 
            visible={true} 
            label={isProcessingOverlay ? "Conferma pagamento..." : "Preparazione pagamento..."} 
          />
        )}
      </AnimatePresence>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6">Completa il pagamento</Typography>
            <Box sx={{ width: 240, mt: 1 }}>
              <AnimatePresence>
                {(isLoading || isProcessingOverlay) ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <LinearProgress />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
          {/* Left: main form */}
          <Box sx={{ flex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                
                {/* Step 0: Selezione numero di attività */}
                {step === 0 && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <BusinessIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={700}>Numero di attività</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Indica per quante attività vuoi attivare l'utenza
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Numero attività</InputLabel>
                      <Select
                        value={numberOfActivities}
                        label="Numero attività"
                        onChange={(e) => setNumberOfActivities(Number(e.target.value))}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                          <MenuItem key={num} value={num}>{num}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button variant="text" onClick={handleClose} sx={{ flex: 1 }}>
                        Annulla
                      </Button>
                      <Button variant="contained" onClick={handleNumberConfirm} sx={{ flex: 2, py: 1.25 }}>
                        Continua
                      </Button>
                    </Box>
                  </>
                )}

                {/* Step 0.5: Form dati per ogni attività */}
                {step === 0.5 && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BusinessIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Attività {currentActivityIndex + 1} di {numberOfActivities}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${currentActivityIndex + 1}/${numberOfActivities}`} 
                        color="primary" 
                        size="small" 
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Inserisci i dati dell'attività
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Indirizzo *"
                          value={activitiesData[currentActivityIndex]?.indirizzo || ""}
                          onChange={(e) => updateActivityData("indirizzo", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Città *"
                          value={activitiesData[currentActivityIndex]?.citta || ""}
                          onChange={(e) => updateActivityData("citta", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="CAP *"
                          value={activitiesData[currentActivityIndex]?.cap || ""}
                          onChange={(e) => updateActivityData("cap", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Provincia *"
                          value={activitiesData[currentActivityIndex]?.provincia || ""}
                          onChange={(e) => updateActivityData("provincia", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Telefono"
                          value={activitiesData[currentActivityIndex]?.telefono || ""}
                          onChange={(e) => updateActivityData("telefono", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={activitiesData[currentActivityIndex]?.email || ""}
                          onChange={(e) => updateActivityData("email", e.target.value)}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button variant="text" onClick={handleActivityBack} sx={{ flex: 1 }}>
                        {currentActivityIndex > 0 ? "Indietro" : "Annulla"}
                      </Button>
                      <Button 
                        variant="contained" 
                        onClick={handleActivitySave} 
                        sx={{ flex: 2, py: 1.25 }}
                        disabled={
                          !activitiesData[currentActivityIndex]?.indirizzo ||
                          !activitiesData[currentActivityIndex]?.citta ||
                          !activitiesData[currentActivityIndex]?.cap ||
                          !activitiesData[currentActivityIndex]?.provincia
                        }
                      >
                        {currentActivityIndex < numberOfActivities - 1 ? "Successiva" : "Completa"}
                      </Button>
                    </Box>
                  </>
                )}

                {/* Step 1: choose payment mode */}
                {step === 1 && (
                  <>
                    <Typography variant="subtitle1" fontWeight={700} mb={1}>Seleziona tipo di pagamento</Typography>
                    <RadioGroup row value={paymentMode} onChange={(e) => setPaymentMode(e.target.value as any)}>
                      <FormControlLabel value="single" control={<Radio />} label={<Box><Typography>Pagamento unico</Typography><Typography variant="caption">€{SINGLE_PAYMENT_AMOUNT} — addebito immediato</Typography></Box>} />
                      <FormControlLabel value="subscription" control={<Radio />} label={<Box><Typography>Abbonamento</Typography><Typography variant="caption">€{MONTHLY_COST} / mese — 12 mesi</Typography></Box>} />
                    </RadioGroup>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button variant="text" onClick={() => setStep(0.5)} sx={{ flex: 1 }}>
                        Indietro
                      </Button>
                      <Box sx={{ flex: 2 }}>
                        {isLoading ? (
                          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                        ) : (
                          <Button variant="contained" fullWidth onClick={startPayment} sx={{ py: 1.25 }}>
                            Procedi al pagamento
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
                  </>
                )}

                {/* Step 2: Stripe Elements */}
                {step === 2 && clientSecret && (
                  <>
                    <Typography variant="subtitle1" fontWeight={700} mb={1}>Inserisci i dati di pagamento</Typography>

                    <Elements stripe={stripePromise}>
                      <CheckoutForm
                        paymentMode={paymentMode}
                        onSuccess={handleLocalSuccess}
                        handleClose={handleClose}
                        setProcessing={(v: boolean) => setIsProcessingOverlay(v)}
                        clientSecret={clientSecret}
                      />
                    </Elements>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="text" onClick={() => { setStep(1); setClientSecret(""); }}>Modifica</Button>
                      <Button variant="outlined" onClick={handleClose}>Chiudi</Button>
                    </Box>
                  </>
                )}

                {/* Success checkmark */}
                <AnimatePresence>
                  {showSuccess && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                        <AnimatedCheckmark />
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Paper>
            </motion.div>
          </Box>

          {/* Right: order summary */}
          <Box
            sx={{
              width: { xs: "100%", md: 320 },
              position: "sticky",
              top: 16,
              alignSelf: "flex-start",
              height: "fit-content",
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: 40, filter: "blur(8px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: 20, filter: "blur(6px)" }}
              transition={{
                duration: 0.55,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Riepilogo ordine</Typography>
                <Divider sx={{ mb: 1 }} />
                
                {/* Mostra info attività solo dopo step 0 */}
                {step >= 0.5 && (
                  <>
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">Attività</Typography>
                      <Typography variant="body2" fontWeight={700}>{numberOfActivities}</Typography>
                    </Box>
                    {step >= 1 && activitiesData.length > 0 && (
                      <Box sx={{ my: 1, maxHeight: 150, overflowY: "auto" }}>
                        {activitiesData.map((activity, idx) => (
                          <Card key={idx} variant="outlined" sx={{ mb: 1, p: 1 }}>
                            <Typography variant="caption" fontWeight={600}>
                              Attività {idx + 1}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {activity.indirizzo}, {activity.citta}
                            </Typography>
                          </Card>
                        ))}
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                  </>
                )}

                {step >= 1 && (
                  <>
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">Tipo</Typography>
                      <Typography variant="body2" fontWeight={700}>{paymentMode === "single" ? "Pagamento unico" : "Abbonamento"}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">Costo base (1 attività)</Typography>
                      <Typography variant="body2">{paymentMode === "single" ? `€${BASE_SINGLE_PAYMENT}` : `€${BASE_MONTHLY_COST}/mese`}</Typography>
                    </Box>
                    {additionalActivities > 0 && (
                      <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">
                          Attività aggiuntive ({additionalActivities} × €{COST_PER_ACTIVITY}
                          {paymentMode === "subscription" && ` / ${SUBSCRIPTION_MONTHS} mesi`})
                        </Typography>
                        <Typography variant="body2">
                          {paymentMode === "single" ? `€${totalActivityCost}` : `€${activityCostPerMonth.toFixed(2)}/mese`}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}>Totale</Typography>
                      <Typography variant="body2" fontWeight={800} color="primary.main">
                        {paymentMode === "single" ? `€${SINGLE_PAYMENT_AMOUNT}` : `€${MONTHLY_COST.toFixed(2)}/mese`}
                      </Typography>
                    </Box>
                    {paymentMode === "subscription" && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Durata</Typography>
                        <Typography variant="body2">12 mesi</Typography>
                      </Box>
                    )}
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  {step < 1 ? "Completa i dati delle attività" : "Pagamento sicuro tramite Stripe"}
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentPageDialog;
